import React, { useEffect, useState } from 'react';
import { Box, Chip, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MenuIcon from '@mui/icons-material/Menu';
import { useNavigate } from 'react-router';

import { colors } from '../../theme/tokens';

import { ADMIN_RETURN_TENANT_KEY, AdminReturnTenantStash } from './AdminLayout';

interface Props {
  isCompact: boolean;
  onOpenDrawer: () => void;
}

/**
 * Top bar for `/admin/*`. Intentionally lighter chrome than the tenant
 * TopBar — no Publish button (Globals publish lives inside a sub-route),
 * and search is deferred to Round 2.
 *
 * Left slot: hamburger on compact breakpoints, plus the "Return to
 * tenant" pill when an `adminReturnTenant` stash is present.
 * Right slot: a title + breathing room for a future search shortcut.
 */
const AdminTopBar: React.FC<Props> = ({ isCompact, onOpenDrawer }) => {
  const navigate = useNavigate();
  const [returnStash, setReturnStash] = useState<AdminReturnTenantStash | null>(null);

  // Read the stash on mount + whenever sessionStorage changes in another
  // tab. Storage events don't fire in the tab that wrote the entry, so
  // the mount read covers the "just arrived from tenant" case.
  useEffect(() => {
    const read = (): AdminReturnTenantStash | null => {
      try {
        const raw = window.sessionStorage.getItem(ADMIN_RETURN_TENANT_KEY);
        return raw ? (JSON.parse(raw) as AdminReturnTenantStash) : null;
      } catch {
        return null;
      }
    };
    setReturnStash(read());
    const onStorage = (e: StorageEvent) => {
      if (e.key === ADMIN_RETURN_TENANT_KEY) setReturnStash(read());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const handleBack = () => {
    if (returnStash) {
      try {
        window.sessionStorage.removeItem(ADMIN_RETURN_TENANT_KEY);
      } catch {
        /* ignore storage failure */
      }
      setReturnStash(null);
      navigate(returnStash.path);
      return;
    }
    // No tenant stash → default back destination is the tenant picker.
    navigate('/');
  };

  const backLabel = returnStash ? `Back to tenant ${returnStash.tenantCode}` : 'Back to tenants';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: isCompact ? 2 : 4,
        pt: 2,
        pb: 0,
        flexWrap: 'wrap',
      }}
    >
      {isCompact && (
        <IconButton
          onClick={onOpenDrawer}
          sx={{ color: colors.textPrimary }}
          aria-label="Open navigation"
        >
          <MenuIcon />
        </IconButton>
      )}

      <Chip
        icon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
        label={backLabel}
        onClick={handleBack}
        clickable
        sx={{
          // Slate outline ties back to the admin-accent token. Keeps
          // the pill visually distinct from brand-yellow tenant chips.
          border: `1px solid ${colors.adminAccent}`,
          backgroundColor: colors.adminAccentSubtle,
          color: colors.adminAccent,
          fontFamily: 'Manrope, sans-serif',
          fontWeight: 600,
          fontSize: 13,
          height: 32,
          '&:hover': {
            backgroundColor: colors.adminAccent,
            color: colors.bgWhite,
            '& .MuiChip-icon': { color: colors.bgWhite },
          },
          '& .MuiChip-icon': { color: colors.adminAccent },
        }}
      />

      {/* Right slot — env strip already announces "Admin View"; per-page
          headings live inside each route's own component. */}
      <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }} />
    </Box>
  );
};

export default AdminTopBar;
